import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, FileText, ChevronDown, Sparkles, X, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First name' },
  { tag: '{{rv_type}}', label: 'RV type' },
  { tag: '{{business_name}}', label: 'Business name' },
  { tag: '{{matches_url}}', label: 'Matches link', highlight: true },
  { tag: '{{application_url}}', label: 'Application link' },
  { tag: '{{tow_match_url}}', label: 'TowIQ link' },
  { tag: '{{cost_calculator_url}}', label: 'Cost calc link' },
  { tag: '{{rv_vs_rv_url}}', label: 'RV vs RV link' },
  { tag: '{{deal_analyzer_url}}', label: 'Deal Analyzer link' },
  { tag: '{{negotiation_coach_url}}', label: 'Negotiation Coach link' },
  { tag: '{{daily_matches_url}}', label: 'Daily Matches link' },
  { tag: '{{buyer_toolkit_url}}', label: 'Buyer Toolkit link' },
  { tag: '{{vip_buying_plan_url}}', label: 'VIP Buying Plan link' },
  { tag: '{{vip_concierge_url}}', label: 'VIP Concierge link' },
  { tag: '{{trade_appraisal_url}}', label: 'Trade Appraisal link' },
  { tag: '{{vip_dashboard_url}}', label: 'VIP dashboard' },
];

// Tags the system fills automatically at send time — anything else left in
// the body as {{...}} must be replaced by the advisor before sending.
const AUTO_TAGS = new Set(MERGE_TAGS.map((t) => t.tag.replace(/[{}]/g, '')));

const EmailCompose = ({ leadId, leadEmail, leadName, isDark, onSent, preloadedTemplate }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [templates, setTemplates] = useState({ defaults: [], custom: [] });
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const bodyRef = useRef(null);

  // Insert merge tag at the textarea cursor position (or append if no focus)
  const insertMergeTag = (tag) => {
    const ta = bodyRef.current;
    if (!ta) {
      setBody((prev) => prev + tag);
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + tag + body.slice(end);
    setBody(next);
    // Move cursor to right after the inserted tag
    requestAnimationFrame(() => {
      const pos = start + tag.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Apply preloaded template when it changes
  useEffect(() => {
    if (preloadedTemplate) {
      setSubject(preloadedTemplate.subject || '');
      setBody(preloadedTemplate.body || '');
      setSelectedTemplateId(preloadedTemplate.id);
    }
  }, [preloadedTemplate]);

  const fetchTemplates = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/email-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const applyTemplate = (template) => {
    setSubject(template.subject);
    setBody(template.body);
    setSelectedTemplateId(template.id);
    setShowTemplates(false);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || isSending) return;
    setIsSending(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/dealer/lead/${leadId}/send-email`,
        { subject, body_html: body, template_id: selectedTemplateId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email sent to ${leadEmail}`);
      setSubject('');
      setBody('');
      setSelectedTemplateId(null);
      if (onSent) onSent();
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const allTemplates = [...templates.defaults, ...templates.custom];

  return (
    <div className={`space-y-3 ${isDark ? 'text-white' : ''}`} data-testid="email-compose">
      {/* Template Selector */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTemplates(!showTemplates)}
          className={`flex items-center gap-1.5 text-xs w-full justify-between ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
          data-testid="template-selector-btn"
        >
          <div className="flex items-center gap-1.5">
            <FileText size={14} />
            {selectedTemplateId ? allTemplates.find(t => t.id === selectedTemplateId)?.name || 'Template' : 'Use a template'}
          </div>
          <ChevronDown size={14} />
        </Button>

        {showTemplates && (
          <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-64 overflow-y-auto ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`} data-testid="template-dropdown">
            {/* Follow-up Series */}
            <div className={`px-3 py-2 text-xs font-semibold uppercase ${isDark ? 'text-gray-400 bg-gray-700/50' : 'text-gray-500 bg-gray-50'}`}>
              RV Match AI Follow-Up Series
            </div>
            {templates.defaults.map((t) => (
              <button
                key={t.id}
                onClick={() => applyTemplate(t)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
                data-testid={`template-option-${t.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#F2A900] font-semibold shrink-0">Day {t.series_day}</span>
                  <span className="truncate">{t.name}</span>
                </div>
              </button>
            ))}

            {templates.custom.length > 0 && (
              <>
                <div className={`px-3 py-2 text-xs font-semibold uppercase border-t ${isDark ? 'text-gray-400 bg-gray-700/50 border-gray-600' : 'text-gray-500 bg-gray-50 border-gray-200'}`}>
                  My Templates
                </div>
                {templates.custom.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
                    data-testid={`template-option-${t.id}`}
                  >
                    {t.name}
                  </button>
                ))}
              </>
            )}

            {allTemplates.length === 0 && (
              <div className="px-3 py-4 text-sm text-center text-gray-400">No templates available</div>
            )}
          </div>
        )}
      </div>

      {/* To field */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <span className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>To:</span>
        <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{leadName ? `${leadName} <${leadEmail}>` : leadEmail}</span>
      </div>

      {/* Subject */}
      <Input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject line..."
        className={`${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
        data-testid="email-subject-input"
      />

      {/* Body */}
      <textarea
        ref={bodyRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your email here. Click a merge tag below to drop it in at your cursor."
        rows={8}
        className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#004E86] ${
          isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : 'border-gray-200'
        }`}
        data-testid="email-body-input"
      />

      {/* Merge Tags — click to insert at cursor */}
      <div className={`flex flex-wrap items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <span className="inline-flex items-center gap-1 font-medium">
          <Sparkles size={12} /> Click to insert:
        </span>
        {MERGE_TAGS.map((m) => (
          <button
            key={m.tag}
            type="button"
            onClick={() => insertMergeTag(m.tag)}
            title={m.tag === '{{matches_url}}'
              ? 'Inserts a magic-link URL — recipient is auto signed-in to their dashboard'
              : `Inserts ${m.tag}`}
            data-testid={`merge-tag-${m.tag.replace(/[{}]/g, '')}`}
            className={`px-2 py-1 rounded transition-colors inline-flex items-center gap-1 font-mono ${
              m.highlight
                ? (isDark ? 'bg-amber-900/40 text-amber-200 border border-amber-700/50 hover:bg-amber-900/60'
                          : 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200')
                : (isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700')
            }`}
          >
            {m.highlight && <Link2 size={10} />}
            {m.tag}
          </button>
        ))}
        <span className="basis-full text-[11px] mt-1 italic">
          Tip: <code className={`px-1 rounded ${isDark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>{'{{matches_url}}'}</code> sends a one-click sign-in link — recipients log straight into their RV matches dashboard. A "View My Matches" button is auto-added to every email.
        </span>
      </div>

      {/* Fill-in warning — advisor-fill tags left in the draft */}
      {(() => {
        const leftover = [...new Set([...(subject + ' ' + body).matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)].map((m) => m[1]))].filter((t) => !AUTO_TAGS.has(t));
        if (leftover.length === 0) return null;
        return (
          <div
            className={`rounded-lg border px-3 py-2 text-xs ${isDark ? 'bg-amber-900/25 border-amber-700/50 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-800'}`}
            data-testid="fill-in-warning"
          >
            <strong>Fill these in before sending:</strong>{' '}
            {leftover.map((t) => `{{${t}}}`).join(', ')} — replace them with real details (e.g. the actual date/time). Sending is blocked until they're gone.
          </div>
        );
      })()}

      {/* Send Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSend}
          disabled={!subject.trim() || !body.trim() || isSending}
          className="bg-[#004E86] hover:bg-[#003d6b] text-white flex items-center gap-2"
          data-testid="send-email-btn"
        >
          {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          Send Email
        </Button>
      </div>
    </div>
  );
};

export default EmailCompose;
